// Sticky banner shown while an admin is impersonating a customer.
// - Red in read-only mode (default).
// - Amber when "Allow edits" is enabled — every write request is still audited.
// Polls /api/admin/impersonate/active every 60s to auto-clear on expiry.
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Shield, X, ExternalLink, Pencil, Lock } from "lucide-react";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Switch } from "./ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

export default function ImpersonationBanner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();
  const [active, setActive] = useState(null);
  const [pendingEditToggle, setPendingEditToggle] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);

  const check = useCallback(async () => {
    let tok = null;
    try { tok = window.sessionStorage.getItem("imp_token"); } catch { /* ignore */ }
    if (!tok) { setActive(null); return; }
    try {
      const r = await fetch(`${API}/admin/impersonate/active`, { credentials: "include" });
      if (!r.ok) { setActive(null); return; }
      const data = await r.json();
      if (data.active) {
        setActive(data);
      } else {
        try { window.sessionStorage.removeItem("imp_token"); } catch { /* ignore */ }
        setActive(null);
        if (data.expired) toast.error("Impersonation session expired");
      }
    } catch { /* network blip — keep prior state */ }
  }, []);

  useEffect(() => {
    check();
    const id = setInterval(check, 60_000);
    return () => clearInterval(id);
  }, [check, location.pathname]);

  const stop = async (returnToAdmin) => {
    try {
      await fetch(`${API}/admin/impersonate/stop`, { method: "POST", credentials: "include" });
    } catch { /* ignore */ }
    try { window.sessionStorage.removeItem("imp_token"); } catch { /* ignore */ }
    setActive(null);
    try { await refreshUser(); } catch { /* ignore */ }
    toast.success("Returned to admin");
    if (returnToAdmin) navigate("/admin/customers", { replace: true });
  };

  const flipEdits = async (newValue) => {
    setSavingToggle(true);
    try {
      const r = await fetch(`${API}/admin/impersonate/allow-edits`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ allow_edits: newValue }),
      });
      if (!r.ok) throw new Error("Failed");
      setActive((a) => (a ? { ...a, allow_edits: newValue } : a));
      toast.success(newValue ? "Edit mode ON — every action is audited" : "Read-only mode restored");
    } catch { toast.error("Could not toggle edit mode"); }
    finally { setSavingToggle(false); setPendingEditToggle(false); }
  };

  if (!active) return null;

  const expiresMs = active.expires_at ? new Date(active.expires_at).getTime() - Date.now() : 0;
  const minsLeft = Math.max(0, Math.floor(expiresMs / 60000));
  const editMode = !!active.allow_edits;

  return (
    <>
      <div
        className={`sticky top-0 z-[60] px-3 py-2 shadow-md ${editMode ? "bg-amber-500 text-white" : "bg-red-600 text-white"}`}
        data-testid="impersonation-banner"
        data-edit-mode={editMode ? "on" : "off"}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            {editMode ? <Pencil size={16} className="flex-shrink-0" /> : <Shield size={16} className="flex-shrink-0" />}
            <p className="text-xs sm:text-sm font-semibold truncate">
              {editMode ? "EDIT MODE · " : ""}Impersonating <span className="font-bold underline">{active.target?.email}</span>
              <span className="hidden sm:inline opacity-90 font-normal"> · {minsLeft}m left</span>
              <span className="hidden lg:inline opacity-80 font-normal">{editMode ? " · Every action audited" : " · Read-only support session"}</span>
            </p>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Allow edits switch */}
            <label className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-bold bg-white/15 rounded-md px-2.5 py-1 cursor-pointer">
              {editMode ? <Pencil size={11} /> : <Lock size={11} />}
              <span>Allow edits</span>
              <Switch
                checked={editMode}
                disabled={savingToggle}
                onCheckedChange={(v) => {
                  if (v) { setPendingEditToggle(true); }
                  else { flipEdits(false); }
                }}
                data-testid="impersonation-allow-edits-toggle"
              />
            </label>
            <button
              onClick={() => stop(true)}
              className="inline-flex items-center gap-1 bg-white/15 hover:bg-white/25 text-white text-[11px] font-bold rounded-md px-2.5 py-1 transition"
              data-testid="impersonation-back-btn"
            >
              <ExternalLink size={11} /> Back to admin
            </button>
            <button
              onClick={() => stop(false)}
              className={`inline-flex items-center gap-1 bg-white text-[11px] font-bold rounded-md px-2.5 py-1 transition ${editMode ? "text-amber-700 hover:bg-amber-50" : "text-red-700 hover:bg-red-50"}`}
              data-testid="impersonation-stop-btn"
            >
              <X size={11} /> Stop
            </button>
          </div>
        </div>
      </div>

      {/* Confirm dialog before enabling Edit Mode */}
      <AlertDialog open={pendingEditToggle} onOpenChange={(o) => { if (!o) setPendingEditToggle(false); }}>
        <AlertDialogContent data-testid="impersonation-edit-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Enable edit mode for this support session?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-gray-600">
                <p>
                  You&apos;re about to <span className="font-semibold text-amber-700">unblock writes</span> while impersonating{" "}
                  <span className="font-semibold text-gray-900">{active.target?.email}</span>.
                </p>
                <ul className="text-xs space-y-1.5 bg-amber-50 rounded-lg p-3 border border-amber-100 list-disc pl-5">
                  <li>Any orders, code regenerations or redemptions you trigger will affect <span className="font-semibold">their</span> account.</li>
                  <li>Every action you take is logged to the impersonation audit trail.</li>
                  <li>You can flip back to read-only at any time.</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="impersonation-edit-cancel">Keep read-only</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => flipEdits(true)}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="impersonation-edit-confirm"
            >
              Enable edits
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
